<template>
  <tr
    v-if="!ocultarFilaComponente"
    :style="estiloJerarquico(nivel, (comp.children?.length ?? 0) > 0)"
    :class="{
      'fila-editando': comp.id_componente === componenteEditandoId,
      'fila-seleccionada': componenteSeleccionado?.id_componente === comp.id_componente,
      'dragging': isDragging,
      'drop-before': isDropTarget && dragState.dropSide === 'before',
      'drop-after': isDropTarget && dragState.dropSide === 'after',
    }"
    :draggable="!esIndicadores && !!mostrarAccionesComponente && totalHermanos > 1"
    @click="seleccionar(comp)"
    @dragstart.stop="onDragStart"
    @dragover.stop.prevent="onDragOver"
    @dragend="onDragEnd"
    @drop.stop.prevent="onDrop"
  >
    <td class="celda-numeracion">
      <div v-if="obtenerNumeracionDisplay()" class="numeracion-simple">
        {{ obtenerNumeracionDisplay() }}
      </div>
      <span v-else>-</span>
    </td>
    <td :style="{ paddingLeft: `${nivel * 48}px` }" class="indentado">
      <span
        v-if="!esIndicadores && mostrarAccionesComponente && totalHermanos > 1"
        class="drag-handle"
        title="Arrastra para reordenar"
        @click.stop
      >⠿</span>
      <span
        v-if="(comp.children?.length ?? 0) > 0"
        @click="toggleExpandido(comp.id_componente!)"
        class="flecha"
        :class="{ expandido: expandido.has(comp.id_componente!) }"
      >
        {{ expandido.has(comp.id_componente!) ? '▼ Ocultar' : '▶ Mostrar' }}
      </span>

      {{ comp.nombre }}
    </td>

    <td class="acciones">
      <!-- Acciones para componentes -->
      <template v-if="!esIndicadores && mostrarAccionesComponente">
        <button :disabled="loading" @click="onModificarClick">Modificar</button>
        <button :disabled="loading" @click="$emit('eliminar', comp)">Eliminar</button>
        <button v-if="puedoAgregarHijo" :disabled="loading" @click="$emit('agregarHijo', { padre: comp, nivel })">➕ Agregar {{ niveles?.[nivel + 1] ?? 'Componente' }}</button>
      </template>

      <!-- Acciones para indicadores -->
      <template v-else-if="esIndicadores && mostrarAccionesComponente">
        <button :disabled="loading" @click="$emit('agregarHijo', { padre: comp, nivel })">➕ Agregar Indicador</button>
        <template v-if="esIndicador(comp)">
          <button v-if="puedeAcceder" @click="$emit('acceder', comp)">Acceder</button>
        </template>
      </template>
    </td>
  </tr>

  <!-- Renderizar hijos de componentes -->
  <template v-if="expandido.has(comp.id_componente!)">
    <FilaComponente
      v-for="(hijo, index) in hijosOrdenados"
      :key="hijo.id_componente"
      :comp="hijo"
      :nivel="nivel + 1"
      :ultimoNivel="ultimoNivel"
      :componenteSeleccionado="componenteSeleccionado"
      @update:componenteSeleccionado="emit('update:componenteSeleccionado', $event)"
      :expandido="expandido"
      :toggleExpandido="toggleExpandido"
      :esIndicadores="esIndicadores"
      :esIndicadorFn="esIndicadorFn"
      :puedeAcceder="puedeAcceder"
      :instrumentosPorIndicadorFn="instrumentosPorIndicadorFn"
      :mostrarBotonCalificar="mostrarBotonCalificar"
      :mostrarAccionesComponente="mostrarAccionesComponente"
      :mostrarAccionesIndicador="mostrarAccionesIndicador"
      :ocultarFilaComponente="ocultarFilaComponente"
      :componenteEditandoId="componenteEditandoId"
      :niveles="niveles"
      :obtenerNumeracion="obtenerNumeracion"
      :posicion="index + 1"
      :totalHermanos="hijosOrdenados.length"
      :loading="loading"
      @modificar="$emit('modificar', $event)"
      @eliminar="$emit('eliminar', $event)"
      @agregarHijo="$emit('agregarHijo', $event)"
      @agregarInstrumento="$emit('agregarInstrumento', $event)"
      @vincularFormulario="$emit('vincularFormulario', $event)"
      @acceder="$emit('acceder', $event)"
      @modificarIndicador="$emit('modificarIndicador', $event)"
      @eliminarIndicador="$emit('eliminarIndicador', $event)"
      @modificarInstrumento="$emit('modificarInstrumento', $event)"
      @eliminarInstrumento="$emit('eliminarInstrumento', $event)"
      @calificarInstrumento="emit('calificarInstrumento', $event)"
      @calificarIndicador="emit('calificarIndicador', $event)"
      @reordenar="emit('reordenar', $event)"
    />
  </template>

  <!-- Indicadores e instrumentos SIEMPRE visibles -->
  <template v-if="esIndicadores">
    <template v-for="indicador in comp.indicadores ?? []" :key="`indicador-${indicador.id_indicador}`">
      <tr class="fila-indicador" :class="{ 'fila-calificar-seleccionada': seleccionCalificar === `indicador-${indicador.id_indicador}` }" @click.stop="seleccionarCalificar(`indicador-${indicador.id_indicador}`)">
        <td>Indicador</td>
        <td :style="{ paddingLeft: `${ocultarFilaComponente ? 0 : (nivel + 1) * 48}px` }" class="indentado">
          {{ indicador.enunciado ?? indicador.nombre }}
        </td>
        <td class="acciones">
          <template v-if="mostrarAccionesIndicador">
            <button :disabled="loading" @click="$emit('modificarIndicador', indicador)">Modificar</button>
            <button :disabled="loading" @click="$emit('eliminarIndicador', indicador)">Eliminar</button>
            <!-- Documental y cuantitativa: la ficha es propia del indicador. -->
            <button
              v-if="!esIndicadorDeOpinion(indicador)"
              :disabled="loading"
              @click="$emit('agregarInstrumento', indicador)"
            >➕ Agregar Instrumento</button>
            <!-- Opinión: el formulario es compartido, así que se vincula uno ya
                 creado y se eligen sus preguntas, en vez de crear otro. -->
            <button
              v-else
              :disabled="loading"
              @click.stop="$emit('vincularFormulario', indicador)"
            >🔗 Vincular Formulario</button>
          </template>
          <button
            v-if="mostrarBotonCalificar"
            class="btn calificar"
            @click.stop="$emit('calificarIndicador', { indicador })"
          >
            Calificar
          </button>
        </td>
      </tr>

      <!-- instrumentos asociados -->
      <tr
        v-for="instrumento in getInstrumentos(indicador.id_indicador)"
        :key="`instrumento-${instrumento.id_instrumento}-ind-${indicador.id_indicador}`"
        class="fila-instrumento"
        :class="{ 'fila-calificar-seleccionada': seleccionCalificar === `instrumento-${instrumento.id_instrumento}` }"
        @click.stop="seleccionarCalificar(`instrumento-${instrumento.id_instrumento}`)"
      >
        <td>Instrumento</td>
        <td :style="{ paddingLeft: `${ocultarFilaComponente ? 24 : (nivel + 2) * 48}px` }" class="indentado">
          {{ instrumento.herramienta }}
        </td>
        <td class="acciones">
          <template v-if="mostrarAccionesIndicador">
            <!-- Solo instrumentos de opinión tienen formulario editable; documental y cuantitativa no. -->
            <button
              v-if="esIndicadorDeOpinion(indicador)"
              :disabled="loading"
              @click="$emit('modificarInstrumento', instrumento)"
            >Modificar</button>
            <button :disabled="loading" @click="$emit('eliminarInstrumento', { instrumento, indicador })">Eliminar</button>
            <button class="btn guardar" @click="verInstrumento(instrumento)">Ver</button>
          </template>
          <button
            v-if="mostrarBotonCalificar"
            class="btn calificar"
            @click.stop="$emit('calificarInstrumento', { instrumento, indicador })"
          >
            Calificar
          </button>
        </td>
      </tr>
    </template>
  </template>
</template>

<script lang="ts" setup>
/**
 * Fila de un componente dentro de las vistas de lista.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, inject, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import type Componente from '@/interfaces/Componente'
import type Indicador from '@/interfaces/Indicador'
import type Instrumento from '@/interfaces/Instrumetos'
import { estiloJerarquico } from '@/utils/estilosJerarquia'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'

const tipoComponenteStore = useGestionTipoComponente()

/**
 * El tipo del indicador sale de `tipo_componentes` (Documental, Cuantitativa,
 * Opinión). Se resuelve por NOMBRE y no por id fijo: los ids dependen del
 * seeder y podrían no ser los mismos en otra base.
 */
function esIndicadorDeOpinion(indicador?: Indicador): boolean {
  if (!indicador?.id_tipo_componente) return false
  const tipo = (tipoComponenteStore.tipos ?? []).find(
    (t: any) => t.id_tipo_componente === indicador.id_tipo_componente
  )
  return /opini/i.test(String(tipo?.nombre ?? ''))
}

const router = useRouter()
const props = withDefaults(defineProps<{
  comp: Componente
  nivel: number
  ultimoNivel: number
  componenteSeleccionado: Componente | null
  expandido: Set<number>
  toggleExpandido: (id: number) => void
  esIndicadores?: boolean
  esIndicadorFn?: (c: Componente) => boolean
  puedeAcceder?: boolean
  instrumentosPorIndicadorFn?: (id_indicador: number) => Instrumento[]
  mostrarBotonCalificar?: boolean
  mostrarAccionesComponente?: boolean
  mostrarAccionesIndicador?: boolean
  ocultarFilaComponente?: boolean
  componenteEditandoId?: number | null
  niveles?: string[]
  obtenerNumeracion?: (comp: Componente, nivel: number) => string
  posicion?: number
  totalHermanos?: number
  loading?: boolean
}>(), {
  mostrarAccionesComponente: true,
  mostrarAccionesIndicador: true,
  ocultarFilaComponente: false,
  posicion: 0,
  totalHermanos: 0,
  loading: false,
})

const emit = defineEmits<{
  'update:componenteSeleccionado': [value: Componente | null]
  'modificar': [comp: Componente]
  'eliminar': [comp: Componente]
  'agregarHijo': [payload: { padre: Componente; nivel: number }]
  'agregarInstrumento': [indicador: Indicador | undefined]
  'vincularFormulario': [indicador: Indicador]
  'acceder': [comp: Componente]
  'modificarIndicador': [indicador: Indicador]
  'eliminarIndicador': [indicador: Indicador]
  'modificarInstrumento': [instrumento: Instrumento]
  'eliminarInstrumento': [payload: { instrumento: Instrumento; indicador: Indicador }]
  'calificarInstrumento': [payload: { instrumento?: Instrumento, indicador?: Indicador }]
  'calificarIndicador': [payload: { indicador: Indicador }]
  'reordenar': [payload: { dragged: Componente; target: Componente; side: 'before' | 'after' }]
}>()

const seleccionCalificar = inject<import('vue').Ref<string | null>>('seleccionCalificar', ref(null))

interface DragState {
  dragging: Componente | null
  dropTargetId: number | null
  dropSide: 'before' | 'after'
}
const dragState = inject<DragState>('dragState', reactive({ dragging: null, dropTargetId: null, dropSide: 'after' as const }))

const isDragging = computed(() => dragState.dragging?.id_componente === props.comp.id_componente)
const isDropTarget = computed(() => dragState.dropTargetId === props.comp.id_componente)
const isValidDropTarget = computed(() =>
  dragState.dragging != null &&
  dragState.dragging.id_componente !== props.comp.id_componente &&
  dragState.dragging.padre_id === props.comp.padre_id
)

function onDragStart(e: DragEvent) {
  dragState.dragging = props.comp
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
}

function onDragOver(e: DragEvent) {
  if (!isValidDropTarget.value) return
  dragState.dropTargetId = props.comp.id_componente!
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  dragState.dropSide = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
}

function onDragEnd() {
  dragState.dragging = null
  dragState.dropTargetId = null
}

function onDrop() {
  if (!dragState.dragging || !isValidDropTarget.value) {
    dragState.dragging = null
    dragState.dropTargetId = null
    return
  }
  const dragged = dragState.dragging
  const side = dragState.dropSide
  dragState.dragging = null
  dragState.dropTargetId = null
  emit('reordenar', { dragged, target: props.comp, side })
}

function seleccionarCalificar(id: string) {
  // Limpiar selección de componentes
  emit('update:componenteSeleccionado', null)
  // Toggle selección actual
  seleccionCalificar.value = seleccionCalificar.value === id ? null : id
}

function getInstrumentos(indicadorId: number): Instrumento[] {
  return props.instrumentosPorIndicadorFn
    ? props.instrumentosPorIndicadorFn(indicadorId)
    : []
}

function seleccionar(comp: Componente) {
  // Limpiar selección de calificar/indicadores/instrumentos
  seleccionCalificar.value = null
  // Toggle selección de componente
  if (props.componenteSeleccionado?.id_componente === comp.id_componente) {
    emit('update:componenteSeleccionado', null)
  } else {
    emit('update:componenteSeleccionado', comp)
  }
}

const esSeleccionado = computed(
  () => props.componenteSeleccionado?.id_componente === props.comp.id_componente
)

const nombreLocal = ref(props.comp.nombre)

watch(
  () => esSeleccionado.value,
  (nuevo) => {
    if (nuevo) {
      nombreLocal.value = props.comp.nombre
    }
  }
)

function esIndicador(c: Componente): boolean {
  if (props.esIndicadorFn) return props.esIndicadorFn(c)
  return false
}

function obtenerNombreNivel(nivel: number): string {
  if (!props.niveles || nivel >= props.niveles.length) {
    return `Nivel ${nivel + 1}`
  }
  return props.niveles[nivel]
}

function obtenerNumeracionDisplay(): string {
  if (!props.obtenerNumeracion) return ''
  return props.obtenerNumeracion(props.comp, props.nivel)
}

// Computed para determinar si se puede agregar un hijo
const puedoAgregarHijo = computed(() => {
  return props.nivel < props.ultimoNivel - 1
})

const hijosOrdenados = computed(() =>
  [...(props.comp.children ?? [])].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
)

function onModificarClick() {
  if (!props.esIndicadores) {
    emit('modificar', props.comp)
  }
}

function verInstrumento(inst: Instrumento) {
  router.push({
    name: 'InstrumentoDetalle',
    params: { id: inst.id_instrumento },
    query: { id_componente: props.comp.id_componente }
  })
}
</script>

<style scoped>
.indentado {
  white-space: normal;
  word-break: break-word
}

.tabla-modern tr:not(.fila-indicador) td.indentado {
  font-weight: 600;   
  color: #222;    
}

.flecha {
  cursor: pointer;
  margin-right: 6px;
  font-weight: 600;
  user-select: none;
}

.acciones {
  display: flex;
  gap: 8px;
}

.acciones button {
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  background-color: #eea222;
  cursor: pointer;
}

.acciones button:hover {
  background-color: #0e69a0;
  color: white;
}

.fila-indicador {
  background-color: #e8f5e9; 
  font-style: italic;
  color: #333;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.fila-indicador:hover:not(.fila-calificar-seleccionada) {
  background-color: #c8e6c9;
}

.fila-indicador td {
  border-top: 1px dashed #ccc;
  padding: 12px 12px;
}

.fila-indicador td.indentado {
  font-weight: 500; 
}

.tabla-modern tr {
  line-height: 1.6;  
}

.tabla-modern td {
  padding: 10px 12px; 
}

.flecha {
  cursor: pointer;
  font-weight: 600;
  margin-right: 6px;
  transition: color 0.3s ease;
}

.flecha {
  color: #FF6F61;
}

.expandido .flecha {
  color: #eea222;
}

.fila-instrumento {
  background-color: #fff9e6; 
  color: #333;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.fila-instrumento:hover:not(.fila-calificar-seleccionada) {
  background-color: #ffecb3;
}

.fila-instrumento td {
  border-top: 1px dashed #ccc;
  padding: 12px 12px;
}

.fila-instrumento td.indentado {
  font-weight: 500;
}

.fila-editando {
  background-color: #e3f2fd !important;
  box-shadow: inset 4px 0 0 #dc3545, 0 0 0 2px #dc3545;
  border-left: 4px solid #dc3545;
  outline: 2px solid #dc3545;
  outline-offset: -2px;
}

.fila-seleccionada {
  background-color: #e3f2fd !important;
  box-shadow: inset 4px 0 0 #dc3545, 0 0 0 2px #dc3545;
  border-left: 4px solid #dc3545;
  outline: 2px solid #dc3545;
  outline-offset: -2px;
}

.fila-calificar-seleccionada {
  background-color: #e3f2fd !important;
  box-shadow: inset 4px 0 0 #dc3545, 0 0 0 2px #dc3545;
  border-left: 4px solid #dc3545;
  outline: 2px solid #dc3545;
  outline-offset: -2px;
}

tr:not(.fila-indicador):not(.fila-instrumento) {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

tr:not(.fila-indicador):not(.fila-instrumento):hover:not(.fila-seleccionada):not(.fila-editando) {
  background-color: #f5f5f5;
}

.fila-indicador,
.fila-instrumento {
  cursor: pointer;
}

.celda-numeracion {
  min-width: 150px;
  text-align: left;
  font-weight: 600;
  color: #0e69a0;
  padding: 10px 12px;
  white-space: nowrap;
}

.drag-handle {
  display: inline-block;
  margin-right: 6px;
  color: #ccc;
  font-size: 16px;
  cursor: grab;
  user-select: none;
  vertical-align: middle;
  transition: color 0.2s;
}

tr:hover .drag-handle {
  color: #0e69a0;
}

tr.dragging {
  opacity: 0.4;
}

tr.drop-before td {
  border-top: 3px solid #0e69a0 !important;
}

tr.drop-after td {
  border-bottom: 3px solid #0e69a0 !important;
}

.numeracion-simple {
  display: inline-block;
}

.componente-encabezado {
  display: flex;
  gap: 1.5rem;
  align-items: baseline;
}

.componente-numeracion {
  color: #0e69a0;
  font-weight: 700;
  white-space: nowrap;
  min-width: 120px;
}

.componente-nombre {
  color: #222;
  font-weight: 600;
  display: inline;
}
</style>